import React from 'react';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { MoreHorizontal, Trash2, Move, Copy, CheckSquare, Square } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { PlotLine, Chapter } from '../types';

interface BulkActionsMenuProps {
  type: 'timelines' | 'chapters';
  items: PlotLine[] | Chapter[];
  selectedItems: PlotLine[] | Chapter[];
  onSelectAll: () => void;
  onClearSelection: () => void;
  onDelete: () => void;
  onReorder: () => void;
  className?: string;
}

export const BulkActionsMenu: React.FC<BulkActionsMenuProps> = ({
  type,
  items,
  selectedItems,
  onSelectAll,
  onClearSelection,
  onDelete,
  onReorder,
  className
}) => {
  const allSelected = items.length > 0 && selectedItems.length === items.length;
  const someSelected = selectedItems.length > 0 && selectedItems.length < items.length;
  
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className={cn(
            "h-8 px-2 flex items-center gap-1.5",
            selectedItems.length > 0 && "bg-accent text-accent-foreground",
            className
          )}
        >
          <MoreHorizontal className="h-4 w-4" />
          {selectedItems.length > 0 && (
            <Badge variant="secondary" className="h-5 px-1.5 text-xs font-normal">
              {selectedItems.length}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {/* Selection options */}
        <DropdownMenuItem 
          onClick={onSelectAll}
          className="flex items-center gap-2"
          disabled={allSelected || items.length === 0}
        >
          <CheckSquare className="h-4 w-4" />
          <span>Select all {type}</span>
        </DropdownMenuItem>
        
        <DropdownMenuItem 
          onClick={onClearSelection}
          className="flex items-center gap-2"
          disabled={selectedItems.length === 0}
        >
          <Square className="h-4 w-4" />
          <span>Clear selection</span>
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        {/* Actions */}
        <DropdownMenuItem 
          onClick={onReorder}
          className="flex items-center gap-2"
          disabled={items.length <= 1}
        >
          <Move className="h-4 w-4" />
          <span>Reorder {type}</span>
        </DropdownMenuItem>
        
        <DropdownMenuItem 
          onClick={onDelete}
          className="flex items-center gap-2 text-destructive focus:text-destructive"
          disabled={selectedItems.length === 0}
        >
          <Trash2 className="h-4 w-4" />
          <span>Delete selected</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
